import React, { useMemo, useState } from 'react';
import { useDeliveredLooseOrders } from './use-delivered-loose-orders';
import Page from 'components/page/page';
import Table, { ColumnsType } from 'antd/lib/table';
import { DeliveredLooseOrderView, FullLooseOrder, UserLevel } from 'types';
import { useQueryValue, useUserLevel } from 'reducers';
import { Link } from 'react-router-dom';
import { Button, Input } from 'antd';
import { PhoneLink } from 'components';
import moment from 'moment';
import { ClaimingModal } from '../orders/orders/big-bag/components/claiming-modal';

export const DeliveredLooseOrders: React.FC = () => {
	const userLevel = useUserLevel();
	const { orders, fetchFullOrder, createClaimingTask } = useDeliveredLooseOrders();
	const [showPerClient, setShowPerClient] = useQueryValue('showPerClient', 'false');
	const [search, setSearch] = useQueryValue('search', '');
	const [claimingOrder, setClaimingOrder] = useState<FullLooseOrder | undefined>();
	const [loadingOrderId, setLoadingOrderId] = useState<number | undefined>();

	const handleMarkForCollection = async (orderId: number) => {
		setLoadingOrderId(orderId);
		const full = await fetchFullOrder(orderId);
		setLoadingOrderId(undefined);
		if (full) {
			setClaimingOrder(full);
		}
	};

	const columns: ColumnsType<DeliveredLooseOrderView> = [
		{
			title: 'Odbiór',
			key: 'mark-claiming',
			fixed: 'left',
			render: (_, order) => {
				if (order.claimingDate != null) {
					return '-';
				}
				return (
					<Button
						type="primary"
						size="large"
						loading={loadingOrderId === order.id}
						onClick={() => handleMarkForCollection(order.id)}
					>
						Odbierz
					</Button>
				);
			},
		},
		{
			title: 'Klient',
			dataIndex: 'client',
			key: 'name',
			render: (_, order) => {
				const isAdmin = (userLevel ?? 0) >= UserLevel.admin;
				if (!isAdmin) {
					return order.client.name;
				}

				return <Link to={`/panel/clients/${order.client.id}`}>{order.client.name}</Link>;
			},
		},
		{
			title: 'Telefon',
			dataIndex: 'client',
			key: 'phone',
			render: (_, order) => <PhoneLink phone={order.contact ?? order.client.phone} />,
		},
		{
			title: 'Ilość',
			dataIndex: 'count',
			key: 'count',
			render: (_, order) => order.count,
		},
		{
			title: 'Podstawiony',
			dataIndex: 'deliveryDate',
			key: 'deliveryDate',
			render: (_, order) => <p>{moment().diff(moment(order.deliveryDate), 'days')} dni temu</p>,
		},
		{
			title: 'Termin odbioru',
			dataIndex: 'claimingDate',
			key: 'claimingDate',
			render: (_, order) => {
				if (!order.claimingDate) {
					return '-';
				}
				return <p>{moment(order.claimingDate).format('DD.MM.YYYY')}</p>;
			},
		},
		{
			title: 'Adres',
			dataIndex: 'address',
			key: 'address',
			render: (_, order) => <Link to={`/panel/orders/${order.id}/loose`}>{order.address}</Link>,
		},
		{
			title: 'Rodzaj odpadu',
			dataIndex: 'wasteType',
			key: 'wasteType',
			render: (_, order) => order.wasteType,
		},
	];

	const columnsPerClient: ColumnsType<DeliveredLooseOrderView[]> = [
		{
			title: 'Klient',
			dataIndex: 'client',
			key: 'name',
			render: (_, orders) => {
				const client = orders[0]!.client;
				const isAdmin = (userLevel ?? 0) >= UserLevel.admin;
				if (!isAdmin) {
					return client.name;
				}

				return <Link to={`/panel/clients/${client.id}`}>{client.name}</Link>;
			},
		},
		{
			title: 'Telefon',
			dataIndex: 'client',
			key: 'phone',
			render: (_, orders) => <PhoneLink phone={orders[0]!.client.phone} />,
		},
		{
			title: 'Ilość',
			dataIndex: 'count',
			key: 'count',
			render: (_, orders) => orders.reduce((sum, order) => sum + order.count, 0),
		},
		{
			title: 'Adres',
			dataIndex: 'address',
			key: 'address',
			render: (_, orders) =>
				orders.map((order, i) => (
					<span key={order.id}>
						<Link to={`/panel/orders/${order.id}/loose`}>{order.address}</Link>
						{i !== orders.length - 1 ? ', ' : ''}
					</span>
				)),
		},
	];

	const filtered = useMemo(() => {
		if (!orders) {
			return undefined;
		}

		const s = search?.toLocaleString().toLocaleLowerCase();
		if (!s) {
			return orders;
		}

		return orders.filter((order) => {
			if (order.client.name.toLocaleLowerCase().includes(s)) {
				return true;
			}

			if (order.address.toLocaleLowerCase().includes(s)) {
				return true;
			}

			return false;
		});
	}, [search, orders]);

	const perClient =
		showPerClient === 'true' &&
		filtered?.reduce((acc, order) => {
			const clientId = order.client.id;
			if (!acc[clientId]) {
				acc[clientId] = [];
			}
			acc[clientId].push(order);

			return acc;
		}, {} as Record<number, DeliveredLooseOrderView[]>);

	return (
		<Page
			title="Podstawione bagi"
			actions={[
				<Button
					key="toggle"
					type="primary"
					size="large"
					onClick={() => setShowPerClient(showPerClient === 'true' ? 'false' : 'true')}
				>
					{showPerClient === 'true' ? 'Pokaż zamówienia osobno' : 'Pokaż sumę na klienta'}
				</Button>,
			]}
		>
			<div className="flex flex-col flex-1 overflow-hidden">
				<div className="mb-4">
					<Input
						size="large"
						placeholder="Szukaj"
						value={search}
						onChange={(e) => setSearch(e.target.value.length === 0 ? undefined : e.target.value)}
					/>
				</div>
				{showPerClient === 'true' ? (
					<Table
						columns={columnsPerClient}
						dataSource={perClient ? Object.values(perClient) : []}
						loading={!perClient}
						pagination={false}
						scroll={{ x: 'max-content' }}
						rowKey={(record) => String(record[0]?.client.id)}
					/>
				) : (
					<Table
						columns={columns}
						dataSource={filtered ?? []}
						loading={!filtered}
						pagination={false}
						scroll={{ x: 'max-content' }}
						rowKey="id"
					/>
				)}
			</div>
			{claimingOrder && (
				<ClaimingModal
					order={claimingOrder}
					visible={!!claimingOrder}
					onCancel={() => setClaimingOrder(undefined)}
					onOk={async ({ date, time, itemsToClaim, price }) => {
						const taskDate = date
							.clone()
							.hours(time?.hours() ?? 0)
							.minutes(time?.minutes() ?? 0)
							.seconds(0)
							.milliseconds(0);
						const ok = await createClaimingTask(claimingOrder.id, { date: taskDate, itemsToClaim, price });
						if (!ok) throw new Error('Nie udało się utworzyć zadania odbioru');
						setClaimingOrder(undefined);
					}}
				/>
			)}
		</Page>
	);
};
